import React from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Bone,
  StretchHorizontal,
  Flame,
  Dumbbell,
  GitCompareArrows,
  ScanLine,
  Trophy,
} from "lucide-react";

const tiles = [
  {
    icon: Activity,
    title: "Corrective Exercise",
    desc: "Restore optimal movement patterns.",
  },
  {
    icon: ScanLine,
    title: "Movement Assessment",
    desc: "Clinical posture & mobility screening.",
  },
  {
    icon: Bone,
    title: "Pain Management",
    desc: "Movement-led relief for chronic pain.",
  },
  {
    icon: StretchHorizontal,
    title: "Posture Correction",
    desc: "Rebuild a tall, balanced posture.",
  },
  {
    icon: Dumbbell,
    title: "Strength Training",
    desc: "Joint-friendly functional strength.",
  },
  {
    icon: Flame,
    title: "Fat Loss",
    desc: "Sustainable body recomposition.",
  },
  {
    icon: GitCompareArrows,
    title: "Mobility Training",
    desc: "Unlock ranges of motion under load.",
  },
  {
    icon: Trophy,
    title: "Athletic Performance",
    desc: "Performance built on a healthy foundation.",
  },
];

export const Specializations = () => {
  return (
    <section
      id="specializations"
      data-testid="specializations-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="aurora" style={{ background: "#00E5FF", width: 600, height: 600, bottom: -200, left: -200, opacity: 0.15 }}></div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">
            Specializations
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
            One system. <span className="text-gradient-blue">Every outcome.</span>
          </h2>
          <p className="mt-4 sm:mt-5 text-zinc-300/85 text-sm sm:text-base lg:text-lg leading-relaxed">
            Each program is custom-built using the same proven framework, applied
            to the goal that matters most to you.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {tiles.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
              data-testid={`spec-tile-${i}`}
              className="group relative aspect-square sm:aspect-[5/4] lg:aspect-square rounded-2xl p-4 sm:p-6 glass glass-hover flex flex-col overflow-hidden"
            >
              <div className="pointer-events-none absolute -bottom-16 -right-16 w-32 h-32 rounded-full bg-[#00E5FF]/0 group-hover:bg-[#00E5FF]/12 blur-2xl transition-colors duration-500"></div>

              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center group-hover:border-[#00E5FF]/30 transition-colors">
                <t.icon className="w-[18px] h-[18px] sm:w-5 sm:h-5 text-[#00E5FF]" strokeWidth={2} />
              </div>

              <div className="mt-auto">
                <div className="font-display text-base sm:text-lg lg:text-xl font-bold text-white leading-snug tracking-tight">
                  {t.title}
                </div>
                <p className="mt-1.5 sm:mt-2 text-[12px] sm:text-sm text-zinc-400 leading-snug line-clamp-3">
                  {t.desc}
                </p>
              </div>

              <div className="absolute top-4 right-4 sm:top-5 sm:right-5 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
                /{(i + 1).toString().padStart(2, "0")}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Specializations;
